/**
 * The application pane's breakpoint vocabulary (Issue #12947).
 *
 * #12946 built the frame: a two-track grid whose first track is the
 * application pane, plus a `ResizeObserver` that knows how wide that pane
 * actually is. What it did not build is a way for a route to SAY something
 * about that width. This module is that vocabulary.
 *
 * ## Why the thresholds are Tailwind's, to the pixel
 *
 * The migration this issue performs is mechanical — `sm:` becomes `pane-sm:`,
 * `md:h-16` becomes `pane-md:h-16` — and it is only safe to perform
 * mechanically if the numbers do not move. A migration that also re-tuned a
 * breakpoint would be indistinguishable, in a 500-file diff, from one that did
 * not. So `pane-md` is 768 and nothing else, pinned by
 * `tests/unit/components/app-frame/app-pane-breakpoints-12947.test.ts`.
 *
 * ## Why a token LIST on one attribute
 *
 * The pane publishes `data-app-pane-w="sm md"` — every breakpoint it has
 * reached — and the CSS variants match with `~=`. One attribute rather than
 * five keeps the React side to a single string, and `~=` needs no `:not()`
 * with a complex selector (still uneven in older Safari).
 *
 * The unmeasured state is the sentinel `"unmeasured"`, NOT `""`. `~=` never
 * matches an empty value, so an empty sentinel would be indistinguishable from
 * "measured, and narrower than `sm`" — and those two states need opposite
 * treatment: unmeasured falls back to the viewport media query (server render,
 * first paint, a component mounted with no frame above it), while
 * measured-and-narrow is a real answer that must be honoured.
 *
 * ## Where the CSS half lives
 *
 * `src/app/globals.css`, `@custom-variant pane-*`. The contract between the
 * two halves — this file's attribute name and threshold table, that file's
 * selectors — has no test since #16550: the e2e spec
 * `tests/e2e/critical-path/golden-path-container-responsive-12947.spec.ts`
 * reads the attribute and checks overflow, never the CSS thresholds. Change
 * the two files in the same commit.
 */

/**
 * Application-pane breakpoints, in px. Identical to Tailwind's default
 * screens, deliberately: `pane-md` should mean to a reader exactly what `md`
 * means, applied to a different box.
 */
export const APP_PANE_BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const

export type AppPaneBreakpoint = keyof typeof APP_PANE_BREAKPOINTS

/** Ascending, which is the order `appPaneWidthTokens` emits. */
export const APP_PANE_BREAKPOINT_ORDER: readonly AppPaneBreakpoint[] = [
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
]

/** The attribute `AppFrame` publishes on the pane element. */
export const APP_PANE_WIDTH_ATTRIBUTE = 'data-app-pane-w'

/**
 * The value of that attribute before the first measurement — and the signal
 * to the CSS variants to defer to the viewport media query instead.
 */
export const APP_PANE_WIDTH_UNMEASURED = 'unmeasured'

/**
 * Whether a measured pane width has reached `breakpoint`.
 *
 * Returns `null` — not `false` — for an unmeasured pane. `false` would assert
 * "the pane is narrower than this", which is a claim nothing can make before
 * the pane has a box. Callers pick their own fallback; `useAppPaneAtLeast`
 * falls back to the browser viewport, which is what every one of these call
 * sites did before it was migrated.
 *
 * A non-positive width counts as UNMEASURED for the same reason. A laid-out
 * pane is never 0 wide — it is a `minmax(0, 1fr)` track of a full-width grid —
 * so 0 means "no box yet": an unattached node, a happy-dom render, the frame
 * between mount and first layout. Reading it as "narrower than sm" would
 * collapse every route to its phone layout for one frame on every page load,
 * which is the flash this whole sentinel exists to prevent.
 */
export function paneMeetsBreakpoint(
  paneWidth: number | null | undefined,
  breakpoint: AppPaneBreakpoint,
): boolean | null {
  if (paneWidth === null || paneWidth === undefined) return null
  if (paneWidth <= 0) return null
  // Floor before comparing: the pane is routinely fractional once a fixed
  // 440px track comes out of an odd viewport width, and a box that is 767.6px
  // wide cannot hold an `md` layout however close it is.
  return Math.floor(paneWidth) >= APP_PANE_BREAKPOINTS[breakpoint]
}

/**
 * The space-separated token list for a measured pane width, or the unmeasured
 * sentinel. `""` is a legitimate result: a pane narrower than `sm`.
 */
export function appPaneWidthTokens(
  paneWidth: number | null | undefined,
): string {
  if (paneWidth === null || paneWidth === undefined || paneWidth <= 0) {
    return APP_PANE_WIDTH_UNMEASURED
  }
  return APP_PANE_BREAKPOINT_ORDER.filter(
    bp => paneMeetsBreakpoint(paneWidth, bp) === true,
  ).join(' ')
}
